// Translate the English / mixed messages returned by the API into Chinese
// user-facing text, keyed by the operation that triggered the request.
//
// The backend still emits English (so logs / tests are stable) — we localize
// here. Unknown messages fall back to the original string so debugging info
// isn't lost.

type Scope = "login" | "register" | "sms" | "reset" | "generation" | "asset" | "default";

interface Rule {
  match: RegExp | ((msg: string) => boolean);
  text: string;
}

const COMMON: Rule[] = [
  // Network
  { match: /failed to fetch|fetch failed|network/i, text: "网络异常，请检查连接后重试" },
  // Auth tokens
  { match: /unauthorized|401/i, text: "登录已过期，请重新登录" },
  { match: /forbidden|403/i, text: "无权访问此操作" },
  { match: /not\s*found|404/i, text: "资源不存在" },
  { match: /rate\s*limit|too many requests|429.*recently/i, text: "操作太频繁，请稍后再试" },
];

const SCOPE_RULES: Record<Scope, Rule[]> = {
  login: [
    { match: /google.*verification.*unavailable|google.*service.*unavailable|google.*relay.*unavailable/i, text: "Google 登录暂时不可用，请使用验证码登录" },
    { match: /google.*credential|google.*audience|google.*email/i, text: "Google 登录失败，请重试" },
    { match: /sms code is incorrect|wrong\s*sms/i, text: "验证码不正确" },
    { match: /sms code (?:not found|expired)/i, text: "验证码已过期，请重新获取" },
    { match: /phone|11.*位|手机号/i, text: "手机号格式不正确" },
    { match: /password|账号|密码/i, text: "手机号或密码错误" },
    { match: /user is unavailable|status/i, text: "账户已被冻结，请联系客服" },
  ],
  register: [
    { match: /already (?:registered|exists)|409|duplicate/i, text: "该手机号已注册，请直接登录" },
    { match: /sms code is incorrect/i, text: "验证码不正确" },
    { match: /sms code (?:not found|expired)/i, text: "验证码已过期，请重新获取" },
    { match: /password.*8|password.*letter|password.*digit/i, text: "密码需 8-32 位，且包含字母与数字" },
    { match: /passwords?.*not match|mismatch/i, text: "两次输入的密码不一致" },
    { match: /phone/i, text: "手机号格式不正确" },
  ],
  sms: [
    { match: /sms code was sent recently|cooldown|429/i, text: "验证码刚发送过，请稍后再试" },
    { match: /phone/i, text: "手机号格式不正确" },
    { match: /sms.*fail|aliyun/i, text: "验证码发送失败，请稍后再试" },
  ],
  reset: [
    { match: /sms code is incorrect/i, text: "验证码不正确" },
    { match: /sms code (?:not found|expired)/i, text: "验证码已过期，请重新获取" },
    { match: /password.*8|password.*letter|password.*digit/i, text: "密码需 8-32 位，且包含字母与数字" },
    { match: /passwords?.*not match|mismatch/i, text: "两次输入的密码不一致" },
    { match: /user.*not found|没有.*账号/i, text: "该手机号尚未注册" },
  ],
  generation: [
    {
      match: /daily credit limit/i,
      text: (msg: string) => msg, // detail filled below
    } as unknown as Rule,
    { match: /credit balance is not enough|insufficient/i, text: "点数余额不足，无法生成" },
    { match: /prompt is required|prompt.*empty/i, text: "请先填写描述" },
    { match: /prompt too long/i, text: "描述太长，请精简到 1500 字以内" },
    { match: /content policy|moderation|blocked/i, text: "内容审核未通过，请修改描述" },
    { match: /no route|provider/i, text: "暂无可用模型路由，请稍后重试" },
    { match: /model_code|aspect_ratio|quality_tier/i, text: "参数不合法，请检查后重试" },
    { match: /reference asset.*not found/i, text: "参考图不存在或已删除" },
  ],
  asset: [
    { match: /name and file_url are required/i, text: "请填写素材名称和图片 URL" },
    { match: /file too large|10 MB/i, text: "图片超过 10 MB，请压缩后上传" },
    { match: /upload.*fail|r2.*fail/i, text: "上传失败，请稍后重试" },
  ],
  default: [],
};

// Custom transform for daily-cap message which includes useful numbers
function dailyCapText(msg: string): string {
  // backend format: "Daily credit limit reached (limit=500, used=123, requested=8). Try again in 24h."
  const m = msg.match(/limit=(\d+).*used=(\d+).*requested=(\d+)/);
  if (m) {
    const [, limit, used, requested] = m;
    return `今日点数已达上限（${limit} 点）。已用 ${used}，本次需 ${requested}。请明日再试。`;
  }
  return "今日点数已达上限，请明日再试。";
}

export function friendlyError(message: string, scope: Scope = "default"): string {
  const text = String(message || "").trim();
  if (!text) return "操作失败，请稍后重试";

  // Scope-specific first
  for (const rule of SCOPE_RULES[scope]) {
    const matched =
      typeof rule.match === "function" ? rule.match(text) : rule.match.test(text);
    if (matched) {
      if (scope === "generation" && /daily credit limit/i.test(text)) {
        return dailyCapText(text);
      }
      return typeof rule.text === "string" ? rule.text : text;
    }
  }
  // Common fallbacks
  for (const rule of COMMON) {
    const matched =
      typeof rule.match === "function" ? rule.match(text) : rule.match.test(text);
    if (matched) return rule.text;
  }
  return text;
}

export const friendlyAuthError = (msg: string, scope: "login" | "register" | "sms" | "reset") =>
  friendlyError(msg, scope);
